import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { 
  Mail, 
  Lock, 
  LogIn, 
  AlertCircle,
  Sparkles,
  TrendingUp,
  Users,
  BarChart3
} from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { user, signIn } = useAuth();
  const { theme } = useTheme();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      // Redirect will happen automatically via AuthProvider
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900' 
        : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'
    }`}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 opacity-30'
            : 'bg-gradient-to-r from-blue-400 to-purple-500 opacity-20'
        }`}></div>
        <div className={`absolute -bottom-40 -left-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-green-600 to-blue-600 opacity-30'
            : 'bg-gradient-to-r from-green-400 to-blue-500 opacity-20'
        }`}></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {/* Left side - Marketing content */}
        <div className="hidden lg:block space-y-8">
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-4xl font-bold bg-clip-text text-transparent ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-white to-gray-300'
                    : 'bg-gradient-to-r from-gray-900 to-gray-600'
                }`}>
                  Welcome to Edyt
                </h1>
                <p className={`text-lg mt-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  AI-powered Customer Success Intelligence
                </p>
              </div>
            </div>
            
            <p className={`text-xl leading-relaxed ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Discover valuable customer insights from your existing communication channels. 
              Connect your tools and start understanding your customers better.
            </p>
          </div>

          {/* Feature highlights */}
          <div className="space-y-4">
            <div className={`flex items-center space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Smart Customer Discovery</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>AI-powered identification from Slack channels</p>
              </div>
            </div>

            <div className={`flex items-center space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Multi-tenant Architecture</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>Secure, isolated workspaces for teams</p>
              </div>
            </div>

            <div className={`flex items-center space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Real-time Analytics</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>Health scores and actionable insights</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <Card className={`backdrop-blur-sm shadow-xl ${
            theme === 'dark'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/80 border-white/20'
          }`}>
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <LogIn className="w-8 h-8 text-white" />
              </div>
              <CardTitle className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Sign in to your account
              </CardTitle>
              <CardDescription className={`${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Access your customer success dashboard
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className={`flex items-center space-x-2 p-4 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-red-900/50 border border-red-800'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-red-300' : 'text-red-700'
                    }`}>{error}</p>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email address
                    </Label>
                    <div className="relative">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="pl-10 h-12"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        className="pl-10 h-12"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Link
                    to="/forgot-password"
                    className={`text-sm font-medium transition-colors ${
                      theme === 'dark'
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-500'
                    }`}
                  >
                    Forgot your password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <LogIn className="w-5 h-5" />
                      <span>Sign in</span>
                    </div>
                  )}
                </Button>
                
                <div className={`text-center pt-4 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Don't have an account?{' '}
                    <Link
                      to="/signup"
                      className={`font-medium transition-colors ${
                        theme === 'dark'
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-500'
                      }`}
                    >
                      Sign up for free
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Trust indicators */}
          <div className="mt-8 text-center">
            <p className={`text-xs mb-2 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>Built for growing teams</p>
            <div className="flex items-center justify-center space-x-6 opacity-60">
              <div className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>Startups</div>
              <div className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>Scale-ups</div>
              <div className={`text-xs font-medium ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>SMBs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;